export { LocaleCode, useAssetOnLocale, useConfigOnLocale, useSwichLocale }

enum LocaleCode {
  ZH = 'zh',
  EN = 'en',
}

function useAssetOnLocale(assets: Record<LocaleCode, Promise<{ default: string }>>) {
  const { locale } = useNuxtApp().$i18n
  const url = ref('')

  watch(
    locale as Ref<LocaleCode>,
    (locale) => {
      assets[locale]?.then((res) => {
        url.value = res.default
      })
    },
    { immediate: true },
  )

  return url
}

function useConfigOnLocale<C>(config: Record<LocaleCode, C>) {
  const { locale } = useI18n()

  return computed(() => (config as any)[locale.value])
}

function useSwichLocale() {
  const { locale, locales, setLocale } = useI18n()
  const switchLocalePath = useSwitchLocalePath()
  const router = useRouter()

  const localeOptions = computed(() =>
    unref(locales).map(l => ({
      value: typeof l === 'string' ? l : l.code,
      label: typeof l === 'string' ? l : l.name,
    })),
  )

  const localeLabel = computed(() => {
    const current = unref(locale)
    const target = unref(localeOptions).find(item => item.value === current)
    return target ? target.label : 'English'
  })

  const switchLanguage = async (lang: string) => {
    if (lang === unref(locale))
      return

    // 先调用 setLocale（它会自动处理 cookie、i18n 状态）
    await setLocale(lang)

    // 再更新路由到当前页面的对应语言版本
    const newPath = switchLocalePath(lang)
    if (newPath && newPath !== router.currentRoute.value.fullPath) {
      await router.replace(newPath)
    }
  }

  /**
   * 不再 watch locale 自动跳转
   * 因为 setLocale() 已经负责处理 URL、cookie 和上下文
   * watch 反而会引起重复跳转和状态错乱
   */

  return {
    locale,
    localeLabel,
    localeOptions,
    switchLanguage,
  }
}
